import React, { useCallback, useRef, useState } from 'react'
import TaskCreatePanel from '@/pages/project/TaskManagement/components/TaskCreatePanel'
import { TaskService } from '@/api/generated/services/TaskService'
import type { TaskFullResponse } from '@/api/generated/models/TaskFullResponse'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'

type InitialTaskData = NonNullable<React.ComponentProps<typeof TaskCreatePanel>['initialTaskData']>

interface TaskEditData {
  taskId: string
  taskSubgroupId: string
  taskSubgroupName: string
  taskGroupName: string
  businessName: string
  businessId: string
  initialTaskData: InitialTaskData
}

interface UseTaskEditFromReportOptions {
  /** 세부실행과제 조회에 성공해 수정 패널을 열기 직전 호출 — 보고서 패널은 수정 패널보다 뒤에 렌더링돼 위를 덮으므로 여기서 닫는다 */
  onBeforeOpen?: () => void
  /** 수정 저장 성공 후 호출 — 목록 재조회 등 */
  onSuccess?: () => void
}

const toTaskEditData = (response: TaskFullResponse): TaskEditData => ({
  taskId: response.id,
  taskSubgroupId: response.task_subgroup_id || '',
  taskSubgroupName: response.task_subgroup_name || '',
  taskGroupName: response.task_group_name || '',
  businessName: response.business_name || '',
  businessId: response.business_id || '',
  initialTaskData: {
    name: response.name || '',
    assigneeId: response.assignee?.id || '',
    assigneeName: response.assignee?.name || '',
    subAssigneeId: response.sub_assignee?.id || '',
    subAssigneeName: response.sub_assignee?.name || '',
    managerId: response.manager?.id || '',
    managerName: response.manager?.name || '',
    reviewerId: response.reviewer?.id || '',
    reviewerName: response.reviewer?.name || '',
    departmentId: response.department_id || '',
    departmentName: response.department_name || '',
    startAt: response.start_at || '',
    endAt: response.end_at || '',
    // description 은 생성 타입(TaskFullResponse)에 없지만 응답에는 실려 온다
    description: (response as TaskFullResponse & { description?: string | null }).description || '',
    judgmentBasis: response.judgment_basis || '',
    budget: response.budget ? {
      id: response.budget.id,
      resultDescription: response.budget.result_description || '',
      items: (response.budget.items || []).map(item => ({
        id: item.id,
        name: item.name || '',
        targetFigure: Number(item.target_figure) || 0,
        doneFigure: Number(item.done_figure) || 0
      }))
    } : undefined,
    performance: response.performance ? {
      id: response.performance.id,
      name: response.performance.name || '',
      unit: response.performance.unit || '',
      items: (response.performance.items || []).map(item => ({
        id: item.id,
        name: item.name || '',
        targetFigure: Number(item.target_figure) || 0,
        doneFigure: Number(item.done_figure) || 0
      }))
    } : undefined,
    labels: (response.labels || []).map(label => ({
      id: label.id,
      key: label.key
    })),
    detailInfos: (response.detail_infos || []).map(info => ({
      id: info.id,
      business_detail_field_id: info.business_detail_field_id,
      key: info.key,
      name: info.name,
      value: info.value
    })),
    extraInfos: (response.extra_infos || []).map(info => ({
      id: info.id,
      business_extra_field_id: info.business_extra_field_id,
      key: info.key,
      name: info.name,
      value: info.value
    }))
  }
})

/**
 * 보고서 상세 패널의 "세부실행과제 수정" 버튼용.
 * taskId로 세부실행과제를 조회해 TaskCreatePanel(editMode)을 연다.
 * 반환된 taskEditPanel 을 페이지 JSX 에 그대로 렌더링하고, openTaskEdit 를 ReportDetailPanel 의 onEditTask 로 넘긴다.
 */
export const useTaskEditFromReport = ({ onBeforeOpen, onSuccess }: UseTaskEditFromReportOptions = {}) => {
  const { showError } = useAlert()
  const [taskEditData, setTaskEditData] = useState<TaskEditData | null>(null)
  const isLoadingRef = useRef(false)

  // 콜백은 매 렌더 바뀌어도 openTaskEdit 를 다시 만들지 않도록 ref 로 최신값만 참조
  const onBeforeOpenRef = useRef(onBeforeOpen)
  const onSuccessRef = useRef(onSuccess)
  onBeforeOpenRef.current = onBeforeOpen
  onSuccessRef.current = onSuccess

  const openTaskEdit = useCallback(async (taskId: string) => {
    if (!taskId || isLoadingRef.current) return
    isLoadingRef.current = true
    try {
      const response = await TaskService.appsTaskViewsGetTask({ taskId })
      onBeforeOpenRef.current?.()
      setTaskEditData(toTaskEditData(response))
    } catch (error) {
      handleApiError(error, showError, '세부실행과제 데이터를 불러오는데 실패했습니다.')
    } finally {
      isLoadingRef.current = false
    }
  }, [showError])

  const handleClose = useCallback(() => {
    setTaskEditData(null)
  }, [])

  const handleSuccess = useCallback(() => {
    setTaskEditData(null)
    onSuccessRef.current?.()
  }, [])

  const taskEditPanel = taskEditData ? (
    <TaskCreatePanel
      isOpen={true}
      onClose={handleClose}
      taskSubgroupId={taskEditData.taskSubgroupId}
      taskSubgroupName={taskEditData.taskSubgroupName}
      taskGroupName={taskEditData.taskGroupName}
      businessName={taskEditData.businessName}
      businessId={taskEditData.businessId}
      editMode={true}
      taskId={taskEditData.taskId}
      initialTaskData={taskEditData.initialTaskData}
      onSuccess={handleSuccess}
    />
  ) : null

  return { openTaskEdit, taskEditPanel }
}
